import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Minus, Search, GripVertical, MoreVertical, Eye, Trash2, Music4, ListMusic, Library as LibraryIcon, FolderOpen, FolderPlus, ImagePlus, FileText, Layers, Save, Loader2, Sparkles, Check, ExternalLink } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import { fileUrl } from "@/components/FileViewer";
import { KeyBadge, OrdenarMenu, transposeKey, sortList } from "@/pages/Library";

const KIND_LABEL = { image: "Imagem", pdf: "PDF", word: "Word" };
const stripSong = (s) => ({ title: s.title, artist: s.artist || "", key: s.key || "", lyrics: s.lyrics || "", chords: s.chords || "", notes: s.notes || "", version_label: s.version_label, song_id: s.song_id, version_id: s.version_id, file_id: s.file_id, file_kind: s.file_kind, file_type: s.file_type, original: s.original, structured: s.structured });
const fromLib = (s) => ({ title: s.title, artist: s.artist || "", key: s.key || "", lyrics: s.lyrics || "", chords: s.chords || "", notes: s.notes || "", song_id: s.id, file_id: s.file_id, file_kind: s.file_kind, file_type: s.file_type, original: s.original, structured: s.structured });

function EventRow({ id, s, index, draggable, onOpen, onTranspose, onSave, onRemove, canSave }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined };
  return (
    <div ref={setNodeRef} style={style} data-testid={`ev-rep-row-${index}`} className="group flex items-center gap-2 sm:gap-3 rounded-xl border border-border bg-surface px-3 py-3 hover:border-accent/40 transition-colors">
      {draggable ? (
        <button {...attributes} {...listeners} data-testid={`ev-rep-drag-${index}`} aria-label="Arrastar para reordenar" className="touch-none cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-foreground shrink-0"><GripVertical className="h-5 w-5" /></button>
      ) : <span className="w-5 shrink-0" />}
      <span className="w-6 text-center text-sm font-semibold text-muted-foreground shrink-0 tabular-nums">{index + 1}</span>
      <button type="button" onClick={onOpen} data-testid={`ev-rep-open-${index}`} className="min-w-0 flex-1 text-left">
        <div className="font-semibold text-[15px] truncate">{s.title || "Sem título"}</div>
        {s.artist && <div className="text-xs text-muted-foreground truncate">{s.artist}</div>}
        {(s.version_label || s.file_id) && (
          <div className="flex items-center gap-1.5 mt-1">
            {s.version_label && <span className="text-[10px] px-1.5 py-0.5 rounded-md border border-accent/30 bg-accent/10 text-accent inline-flex items-center gap-1"><Layers className="h-3 w-3" />{s.version_label}</span>}
            {s.file_id && <span className="text-[10px] px-1.5 py-0.5 rounded-md border border-info/40 bg-info/15 text-info inline-flex items-center gap-1"><FileText className="h-3 w-3" />{KIND_LABEL[s.file_kind] || "Arquivo"}</span>}
          </div>
        )}
      </button>
      <KeyBadge k={s.key} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" data-testid={`ev-rep-menu-${index}`} aria-label="Mais ações" className="h-8 w-8 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-surface-hover shrink-0"><MoreVertical className="h-4 w-4" /></button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={onOpen} data-testid={`ev-rep-view-${index}`} className="gap-2"><Eye className="h-4 w-4" />Ver cifra</DropdownMenuItem>
          {!s.file_id && <DropdownMenuItem onClick={() => onTranspose(1)} data-testid={`ev-rep-up-${index}`} className="gap-2"><Plus className="h-4 w-4" />Subir ½ tom</DropdownMenuItem>}
          {!s.file_id && <DropdownMenuItem onClick={() => onTranspose(-1)} data-testid={`ev-rep-down-${index}`} className="gap-2"><Minus className="h-4 w-4" />Descer ½ tom</DropdownMenuItem>}
          {canSave && <DropdownMenuItem onClick={onSave} data-testid={`ev-rep-save-${index}`} className="gap-2"><Save className="h-4 w-4" />Salvar na biblioteca</DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onRemove} data-testid={`ev-rep-remove-${index}`} className="gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Remover</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default function EventRepertoire({ songs = [], onChange }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("manual");
  const [viewerIdx, setViewerIdx] = useState(null);
  // pickers
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const [libQ, setLibQ] = useState("");
  const [libChecks, setLibChecks] = useState({});
  const [setOpen, setSetOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  // manual
  const [manual, setManual] = useState(null); // { title, artist, key, lyrics }
  // save as setlist
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  // file import
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [origMeta, setOrigMeta] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const viewerEntry = viewerIdx != null ? songs[viewerIdx] : null;
  const stepViewer = (d) => setViewerIdx((i) => { if (i == null) return i; const ni = i + d; return ni >= 0 && ni < songs.length ? ni : i; });
  const vTouch = useRef({ x: 0, y: 0 });
  useEffect(() => {
    if (viewerIdx == null) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); stepViewer(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); stepViewer(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewerIdx, songs.length]); // eslint-disable-line

  const rows = useMemo(() => (songs || []).map((s, idx) => ({ s, idx })).filter(({ s }) => !q || (s.title || "").toLowerCase().includes(q.toLowerCase()) || (s.artist || "").toLowerCase().includes(q.toLowerCase())), [songs, q]);
  const draggable = sort === "manual" && !q;

  const applySort = (mode) => { setSort(mode); if (mode !== "manual") onChange(sortList(songs, mode)); };
  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id || !draggable) return;
    onChange(arrayMove(songs, Number(active.id), Number(over.id)));
  };
  const transpose = (idx, d) => onChange(songs.map((s, i) => (i === idx ? { ...s, key: transposeKey(s.key, d) } : s)));
  const remove = (idx) => onChange(songs.filter((_, i) => i !== idx));
  const saveToLib = async (s) => {
    if (!s.title?.trim()) return toast.error("Informe o título antes de salvar na biblioteca.");
    try { await api.post("/songs", { title: s.title, artist: s.artist || "", key: s.key || "", lyrics: s.lyrics || "", chords: s.chords || "", notes: s.notes || "", tags: [] }); toast.success("Salva no Meu Repertório."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- add from library ----
  const openLib = async () => { setLibChecks({}); setLibQ(""); setLibOpen(true); try { const { data } = await api.get("/songs"); setLib(data); } catch { toast.error("Não foi possível carregar sua biblioteca."); } };
  const libFiltered = lib.filter((s) => !libQ || s.title.toLowerCase().includes(libQ.toLowerCase()) || (s.artist || "").toLowerCase().includes(libQ.toLowerCase()));
  const confirmLib = () => {
    const picked = lib.filter((s) => libChecks[s.id]);
    if (!picked.length) return toast.error("Selecione ao menos uma música.");
    onChange([...songs, ...picked.map(fromLib)]);
    toast.success(`${picked.length} música(s) adicionada(s).`);
    setLibOpen(false); setLibChecks({});
  };

  // ---- import setlist ----
  const openSet = async () => { setSetOpen(true); try { const { data } = await api.get("/repertoires"); setGroups(data); } catch { toast.error("Não foi possível carregar os setlists."); } };
  const importGroup = (g) => { onChange([...songs, ...(g.songs || []).map(stripSong)]); setSetOpen(false); toast.success(`Setlist "${g.name}" adicionado (${(g.songs || []).length} música(s)).`); };

  // ---- manual ----
  const saveManual = () => {
    if (!manual.title.trim()) return toast.error("Informe o título da música.");
    onChange([...songs, { title: manual.title.trim(), artist: manual.artist.trim(), key: manual.key.trim(), lyrics: manual.lyrics, chords: "", notes: "" }]);
    toast.success(`"${manual.title.trim()}" adicionada.`);
    setManual(null);
  };

  // ---- save as setlist ----
  const saveAsSetlist = async () => {
    const valid = songs.filter((s) => s.title?.trim()).map(stripSong);
    if (!valid.length) return toast.error("Adicione ao menos uma música com título.");
    if (!saveName.trim()) return toast.error("Informe o nome do setlist.");
    try { await api.post("/repertoires", { name: saveName.trim(), songs: valid }); toast.success(`Setlist "${saveName.trim()}" salvo! Reutilize em qualquer evento.`); setSaveOpen(false); setSaveName(""); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // ---- file import ----
  const pickFile = (e) => { const f = e.target.files?.[0]; e.target.value = ""; if (!f) return; setPending(f); setOrigMeta(null); };
  const runAI = async () => {
    const file = pending; setPending(null); setExtracting(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/repertoire/extract", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange([...songs, ...data.songs]); toast.success(`${data.songs.length} música(s) importada(s).`); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setExtracting(false); }
  };
  const startOriginal = () => setOrigMeta({ title: (pending?.name || "").replace(/\.[^.]+$/, ""), artist: "", key: "" });
  const uploadOriginal = async () => {
    if (!origMeta.title.trim()) return toast.error("Informe o nome da música.");
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", pending); fd.append("title", origMeta.title); fd.append("artist", origMeta.artist); fd.append("key", origMeta.key); const { data } = await api.post("/songs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange([...songs, data]); toast.success("Arquivo salvo no repertório!"); setOrigMeta(null); setPending(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const inp = "bg-background border-border rounded-xl";
  const busy = extracting || uploading;

  return (
    <div className="space-y-3" data-testid="event-repertoire">
      <input ref={fileRef} type="file" accept="image/*,.pdf,application/pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" className="hidden" onChange={pickFile} />

      {/* Barra de ações */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="ev-rep-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título ou artista..." className={`${inp} pl-9`} />
        </div>
        <OrdenarMenu value={sort} onChange={applySort} testid="ev-rep-sort" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button data-testid="ev-rep-add" className="rounded-xl gap-1.5 shrink-0" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Adicionar</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={openLib} data-testid="ev-rep-add-lib" className="gap-2"><LibraryIcon className="h-4 w-4 text-accent" />Da Biblioteca</DropdownMenuItem>
            <DropdownMenuItem onClick={openSet} data-testid="ev-rep-add-setlist" className="gap-2"><FolderOpen className="h-4 w-4 text-[#FF4D00]" />De um setlist</DropdownMenuItem>
            <DropdownMenuItem onClick={() => setManual({ title: "", artist: "", key: "", lyrics: "" })} data-testid="ev-rep-add-manual" className="gap-2"><Plus className="h-4 w-4" />Adicionar manual</DropdownMenuItem>
            <DropdownMenuItem onClick={() => fileRef.current?.click()} data-testid="ev-rep-add-file" className="gap-2"><ImagePlus className="h-4 w-4" />Importar arquivo (Foto/PDF/Word)</DropdownMenuItem>
            {songs.length > 0 && <><DropdownMenuSeparator /><DropdownMenuItem onClick={() => { setSaveName(""); setSaveOpen(true); }} data-testid="ev-rep-save-setlist" className="gap-2"><FolderPlus className="h-4 w-4" />Salvar como setlist</DropdownMenuItem></>}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {busy && <p className="text-xs text-accent flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" />{extracting ? "Lendo com a IA..." : "Enviando arquivo..."}</p>}

      {/* Lista */}
      {(songs || []).length === 0 ? (
        <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-10 text-center flex flex-col items-center gap-2" data-testid="ev-rep-empty"><Music4 className="h-8 w-8 opacity-60" />Nenhuma música ainda. Toque em "Adicionar" para escolher da Biblioteca, de um setlist ou importar.</div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground bg-surface border border-dashed border-border rounded-xl p-8 text-center">Nenhuma música corresponde à busca.</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={rows.map((r) => String(r.idx))} strategy={verticalListSortingStrategy}>
            <div className="space-y-2" data-testid="ev-rep-rows">
              {rows.map(({ s, idx }) => (
                <EventRow key={idx} id={String(idx)} s={s} index={idx} draggable={draggable}
                  canSave={!s.song_id && !s.file_id}
                  onOpen={() => setViewerIdx(idx)} onTranspose={(d) => transpose(idx, d)} onSave={() => saveToLib(s)} onRemove={() => remove(idx)} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Visualizar cifra/arquivo com navegação anterior/próxima */}
      <Dialog open={viewerIdx != null} onOpenChange={(o) => !o && setViewerIdx(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ev-rep-viewer"
          onTouchStart={(e) => { const t = e.touches[0]; vTouch.current = { x: t.clientX, y: t.clientY }; }}
          onTouchEnd={(e) => { const t = e.changedTouches[0]; const dx = t.clientX - vTouch.current.x, dy = t.clientY - vTouch.current.y; if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) stepViewer(dx < 0 ? 1 : -1); }}>
          <DialogHeader><DialogTitle>{viewerEntry?.title}{viewerEntry?.key ? ` · Tom ${viewerEntry.key}` : ""}</DialogTitle></DialogHeader>
          {songs.length > 1 && (
            <div className="flex flex-col items-center gap-1" data-testid="ev-rep-viewer-pagination">
              <div className="flex items-center gap-1.5 max-w-full overflow-x-auto no-scrollbar px-1 py-1">
                {songs.map((_, i) => (
                  <button key={i} type="button" data-testid={`ev-rep-viewer-dot-${i}`} onClick={() => setViewerIdx(i)} aria-label={`Ir para música ${i + 1}`} aria-current={i === viewerIdx}
                    className={`h-2.5 rounded-full shrink-0 transition-all ${i === viewerIdx ? "w-6 bg-accent" : "w-2.5 bg-muted-foreground/30 hover:bg-muted-foreground/60"}`} />
                ))}
              </div>
              <span className="text-[11px] text-muted-foreground">{(viewerIdx ?? 0) + 1} / {songs.length} · deslize ou use ← →</span>
            </div>
          )}
          {viewerEntry && (
            viewerEntry.file_id ? (
              viewerEntry.file_kind === "image" ? <img src={fileUrl(viewerEntry.file_id)} alt={viewerEntry.title} className="max-h-[65vh] w-full object-contain rounded-lg bg-black" /> :
              viewerEntry.file_kind === "pdf" ? <iframe title={viewerEntry.title} src={fileUrl(viewerEntry.file_id)} className="w-full h-[70vh] rounded-lg bg-white" /> :
              <div className="py-10 text-center space-y-4"><FileText className="h-12 w-12 text-accent mx-auto" /><p className="text-muted-foreground text-sm">A visualização de documentos Word não é suportada no navegador.</p><a href={fileUrl(viewerEntry.file_id)} target="_blank" rel="noreferrer"><Button className="rounded-xl gap-2"><ExternalLink className="h-4 w-4" />Abrir documento</Button></a></div>
            ) : viewerEntry.structured ? <ChordSheetViewer structured={viewerEntry.structured} music={viewerEntry.title} artist={viewerEntry.artist} /> :
            <pre className="whitespace-pre-wrap font-mono text-sm text-foreground">{viewerEntry.chords || viewerEntry.lyrics || "Sem conteúdo."}</pre>
          )}
        </DialogContent>
      </Dialog>

      {/* Picker Biblioteca */}
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-w-md" data-testid="ev-rep-lib-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LibraryIcon className="h-5 w-5 text-accent" />Adicionar da Biblioteca</DialogTitle><DialogDescription>Selecione músicas da sua biblioteca para este evento.</DialogDescription></DialogHeader>
          <Input data-testid="ev-rep-lib-search" value={libQ} onChange={(e) => setLibQ(e.target.value)} placeholder="Buscar por título ou artista" className={inp} />
          <div className="max-h-72 overflow-y-auto space-y-1.5 mt-2">
            {libFiltered.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma música na sua biblioteca.</p> :
              libFiltered.map((s) => (
                <label key={s.id} data-testid={`ev-rep-lib-item-${s.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface hover:border-accent/40 cursor-pointer transition-colors">
                  <Checkbox data-testid={`ev-rep-lib-check-${s.id}`} checked={!!libChecks[s.id]} onCheckedChange={(v) => setLibChecks((c) => ({ ...c, [s.id]: !!v }))} />
                  <span className="min-w-0 flex-1"><span className="block font-medium text-sm truncate">{s.title}</span>{s.artist && <span className="block text-xs text-muted-foreground truncate">{s.artist}</span>}</span>
                  <KeyBadge k={s.key} />
                </label>
              ))}
          </div>
          <Button data-testid="ev-rep-lib-confirm" onClick={confirmLib} className="w-full rounded-xl gap-2"><Check className="h-4 w-4" />Adicionar selecionadas</Button>
        </DialogContent>
      </Dialog>

      {/* Picker Setlist */}
      <Dialog open={setOpen} onOpenChange={setSetOpen}>
        <DialogContent className="max-w-md" data-testid="ev-rep-setlist-modal">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderOpen className="h-5 w-5 text-[#FF4D00]" />Usar um setlist</DialogTitle><DialogDescription>As músicas são copiadas — editar aqui não altera o setlist original.</DialogDescription></DialogHeader>
          <div className="max-h-72 overflow-y-auto space-y-1.5 mt-1">
            {groups.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">Nenhum setlist ainda. Crie em "Meu Repertório → Setlists".</p> :
              groups.map((g) => (
                <button key={g.id} type="button" data-testid={`ev-rep-setlist-item-${g.id}`} onClick={() => importGroup(g)} className="w-full text-left rounded-lg border border-border bg-surface px-3 py-2.5 hover:border-accent/40 transition-colors flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/15 text-accent shrink-0"><ListMusic className="h-4 w-4" /></span>
                  <span className="min-w-0"><span className="block font-medium text-sm truncate">{g.name}</span><span className="block text-xs text-muted-foreground">{(g.songs || []).length} música(s){g.description ? ` · ${g.description}` : ""}</span></span>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Adicionar manual */}
      <Dialog open={!!manual} onOpenChange={(o) => !o && setManual(null)}>
        <DialogContent className="max-w-md" data-testid="ev-rep-manual-modal">
          <DialogHeader><DialogTitle>Adicionar música manual</DialogTitle><DialogDescription>Informe ao menos o título. Letra/cifra é opcional.</DialogDescription></DialogHeader>
          {manual && (
            <div className="space-y-3">
              <Input data-testid="ev-rep-manual-title" value={manual.title} onChange={(e) => setManual({ ...manual, title: e.target.value })} placeholder="Título *" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="ev-rep-manual-artist" value={manual.artist} onChange={(e) => setManual({ ...manual, artist: e.target.value })} placeholder="Artista" className={inp} />
                <Input data-testid="ev-rep-manual-key" value={manual.key} onChange={(e) => setManual({ ...manual, key: e.target.value })} placeholder="Tom" className={inp} />
              </div>
              <Textarea data-testid="ev-rep-manual-lyrics" value={manual.lyrics} onChange={(e) => setManual({ ...manual, lyrics: e.target.value })} placeholder="Letra / cifra (opcional)" className={`${inp} font-mono text-sm min-h-[100px]`} />
              <Button data-testid="ev-rep-manual-save" onClick={saveManual} className="w-full rounded-xl gap-2"><Check className="h-4 w-4" />Adicionar ao repertório</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Salvar como setlist */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="ev-rep-savelist-modal">
          <DialogHeader><DialogTitle>Salvar como setlist</DialogTitle><DialogDescription>Crie um setlist reutilizável com as {songs.filter((s) => s.title?.trim()).length} música(s) atuais.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="ev-rep-savelist-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Nome do setlist *" className={inp} />
            <Button data-testid="ev-rep-savelist-confirm" onClick={saveAsSetlist} className="w-full rounded-xl gap-2"><FolderPlus className="h-4 w-4" />Salvar setlist</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Escolha importar arquivo */}
      <Dialog open={!!pending && !origMeta} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-md" data-testid="ev-rep-import-choice">
          <DialogHeader><DialogTitle>Como deseja importar?</DialogTitle><DialogDescription className="truncate">{pending?.name}</DialogDescription></DialogHeader>
          <div className="grid gap-3">
            <button type="button" data-testid="ev-rep-import-ai" onClick={runAI} className="text-left rounded-xl border border-accent/40 bg-surface p-4 hover:border-accent transition-colors">
              <div className="font-semibold text-accent flex items-center gap-2"><Sparkles className="h-4 w-4" />Converter com IA</div>
              <div className="text-xs text-muted-foreground mt-1">A IA lê o arquivo e cria título, letra e cifra editáveis.</div>
            </button>
            <button type="button" data-testid="ev-rep-import-original" onClick={startOriginal} className="text-left rounded-xl border border-border bg-surface p-4 hover:border-white/30 transition-colors">
              <div className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4" />Manter arquivo original</div>
              <div className="text-xs text-muted-foreground mt-1">Salva o arquivo como enviado. Basta informar o nome.</div>
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detalhes arquivo original */}
      <Dialog open={!!origMeta} onOpenChange={(o) => !o && (setOrigMeta(null), setPending(null))}>
        <DialogContent className="max-w-md" data-testid="ev-rep-orig-modal">
          <DialogHeader><DialogTitle>Arquivo original</DialogTitle><DialogDescription>Informe ao menos o nome da música.</DialogDescription></DialogHeader>
          {origMeta && (
            <div className="space-y-3">
              <Input data-testid="ev-rep-orig-title" value={origMeta.title} onChange={(e) => setOrigMeta({ ...origMeta, title: e.target.value })} placeholder="Nome da música *" className={inp} />
              <div className="grid grid-cols-2 gap-2">
                <Input data-testid="ev-rep-orig-artist" value={origMeta.artist} onChange={(e) => setOrigMeta({ ...origMeta, artist: e.target.value })} placeholder="Artista (opcional)" className={inp} />
                <Input data-testid="ev-rep-orig-key" value={origMeta.key} onChange={(e) => setOrigMeta({ ...origMeta, key: e.target.value })} placeholder="Tom (opcional)" className={inp} />
              </div>
              <Button data-testid="ev-rep-orig-save" onClick={uploadOriginal} disabled={uploading} className="w-full rounded-xl gap-2">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}Salvar no repertório</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
